import React, { useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { toggleOverlay, toggleSideDrawer } from '@redux/header/actions';

import Container from '@common/Container';
import Heading from '@common/Heading';
import Section from '@common/Section';
import Row from '@common/Row';
import Img from '@common/Image';
import Button from '@common/Button';
import Link from '@common/Link';
import RichText from '@common/RichText';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCircle, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import * as styles from './users-speakers.module.scss';

const UsersSpeakers = ({ data }) => {
  const {
    title,
    showTitle,
    list,
    bgColour
  } = data;
  const sessions = useStaticQuery(graphql`{
    allContentfulEvent {
      edges {
        node {
          id
          slug
          title
          speakers {
            id
          }
          guestSpeakers {
            id
          }
        }
      }
    }
  }`);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [speakerSessions, setSpeakerSessions] = useState(null);
  const dispatch = useDispatch();
  const header = useSelector(state => state.header);
  const handleOnClick = (id) => {
    const newSpeaker = list.find(x => x.id === id);
    if (newSpeaker) {
      setActiveSpeaker(newSpeaker);
      dispatch(toggleSideDrawer(true));
      dispatch(toggleOverlay(true));
    }
  }
  const handleClose = () => {
    setActiveSpeaker(null);
    dispatch(toggleOverlay(false));
    dispatch(toggleSideDrawer(false));
  }
  useEffect(() => {
  if (activeSpeaker) {
    const activeSpeakerSessions = sessions.allContentfulEvent.edges.filter(session => session.node.speakers?.find(speaker => speaker.id === activeSpeaker.id) || session.node.guestSpeakers?.find(speaker => speaker.id === activeSpeaker.id))
    if (activeSpeakerSessions) {
      setSpeakerSessions(activeSpeakerSessions);
    }
  }
  },[activeSpeaker]);
  return (
    <>
      <Section className={clsx({[`bg-${bgColour} py-5`]: bgColour })}>
        <Container>
          {(showTitle && title) && <Heading type='h2' className='mx-auto mb-4 col-lg-10 text-center'>{title}</Heading>}
          {list &&
            <Row className='row-cols-lg-2 gx-lg-5'>
              {list.filter(user => !!user.image && !!user.name && !!user.title).map((user, index) => (
                <div key={index}>
                  <div className='border-bottom border-black py-5'>
                    <Row className={clsx('d-flex flex-column flex-sm-row', styles.userCard)} onClick={() => handleOnClick(user.id)}>
                      <div className='col col-md-5 mb-3 mb-md-0'>
                        <Img
                          loading='eager'
                          image={user.image.gatsbyImageData}
                          file={user.image.file}
                          alt={user.image.description}
                          style={{ aspectRatio: '1/1', width: '100%' }}
                          className='rounded'
                        />
                      </div>
                      <div className='col d-flex align-items-center'>
                        <div className='text-start'>
                          <p className='fs-24 fw-medium mb-1 mb-lg-3'>{user.name}</p>
                          <p className='fs-14'>
                            <span>{user.title}</span>
                            {user.company && <span>{` @ ${user.company}`}</span>}
                          </p>
                          <div className='d-lg-none fs-14 mt-3 text-uppercase'>
                            <span className='me-1'>{'Show Bio'}</span><FontAwesomeIcon icon={faChevronRight} size='sm'/>
                          </div>
                        </div>
                      </div>
                    </Row>
                  </div>
                </div>
              ))}
            </Row>
          }
        </Container>
      </Section>
      <div id='drawer' className={clsx(styles.drawer, {[styles.show]:header.showSideDrawer})}>
        {activeSpeaker &&
          <>
            <Row className='bg-teal'>
              <div className='col-5 p-0'>
                {activeSpeaker.image &&
                  <Img
                    loading='eager'
                    image={activeSpeaker.image.gatsbyImageData}
                    file={activeSpeaker.image.file}
                    alt={activeSpeaker.image.description}
                    style={{ aspectRatio: '1/1', width: '100%' }}
                  />
                }
              </div>
              <div className='col d-flex align-items-center position-relative'>
                <Button type='button' className={styles.drawerClose} onClick={handleClose}>
                  <span className='fa-layers fa-fw'>
                    <FontAwesomeIcon icon={faCircle} color='#fff' size='2x' style={{opacity: 0.35}}/>
                    <FontAwesomeIcon icon={faTimes} color='#000' transform='right-6'/>
                  </span>
                  <span className='sr-only sr-only-focusable'>
                    {'Close banner'}
                  </span>
                </Button>
                <div className='ps-1 ps-md-2 ps-xl-4 text-start'>
                  {activeSpeaker.name && <p className={clsx('fw-medium mb-1 mb-lg-3', styles.userName)}>{activeSpeaker.name}</p>}
                  {activeSpeaker.title &&
                    <p className={styles.userTitle}>
                      <span>{activeSpeaker.title}</span>
                      {activeSpeaker.company && <span>{` @ ${activeSpeaker.company}`}</span>}
                    </p>
                  }
                </div>
              </div>
            </Row>
            <Row className='flex-column flex-md-row mt-3 mt-lg-5 mx-1 mx-lg-5 pb-5 border-bottom border-black'>
              <div className='col-3 mb-3 mb-lg-0 text-uppercase'>
                {'Bio'}
              </div>
              <div className='col'>
                  {activeSpeaker.educatorBio &&
                    <RichText json={activeSpeaker.educatorBio}/>
                  }
              </div>
            </Row>
            {speakerSessions &&
              <Row className='flex-column flex-md-row mt-3 mt-lg-5 mx-1 mx-lg-5'>
                <div className='col-3 mb-3 mb-lg-0 text-uppercase'>
                  {'Sessions'}
                </div>
                <div className='col d-flex flex-column'>
                  {speakerSessions.map((session, index) => (
                    <Link key={index} to={`/bts-summit/agenda${session.node.slug}`} onClick={handleClose} className='text-black fw-normal text-decoration-underline mb-3'>{session.node.title}</Link>
                  ))}
                </div>
              </Row>
            }
          </>
        }
      </div>
    </>
  )
}

export default UsersSpeakers
